"use client"

import { Image } from "@/app/components/image"
import type { Blockchain } from "@anaconda/core/blockchain/types"
import { Trans } from "@lingui/react/macro"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from "@rarible_int/anaconda_ui_components"
import { SelectItem } from "@rarible_int/anaconda_ui_components"
import { AnimatePresence, motion } from "motion/react"
import { Suspense, memo } from "react"
import { ErrorBoundary } from "react-error-boundary"
import { BlockchainIcon } from "../blockchain-icon/blockchain-icon"
import { getFeaturedBlockchain } from "../featured-blockchain/get-featured-blockchain"
import { useBlockchain } from "../use-blockchain"
import type { BlockchainFilter as BlockchainFilterType } from "./blockchain-filters-scope"

interface BlockchainFilterProps {
  blockchain: BlockchainFilterType
  isComingSoon?: boolean
  isCompact?: boolean
  isSelected?: boolean
}

// Hook wrapper returns null for "ALL"
export function useBlockchainConfig(blockchain: BlockchainFilterType) {
  const isAllBlockchain = blockchain === "ALL"

  const blockchainConfig = useBlockchain(
    isAllBlockchain ? getFeaturedBlockchain() : (blockchain as Blockchain),
  )
  return isAllBlockchain ? null : blockchainConfig
}

export function BlockchainFilter({ blockchain }: BlockchainFilterProps) {
  return (
    <ErrorBoundary fallback={null}>
      <Suspense>
        <BlockchainFilterView blockchain={blockchain} isComingSoon={false} />
      </Suspense>
    </ErrorBoundary>
  )
}

export const BlockchainFilterView = memo(
  function BlockchainFilterView({
    blockchain,
    isComingSoon = false,
    isCompact = false,
    isSelected = false,
  }: BlockchainFilterProps) {
    const isAllBlockchain = blockchain === "ALL"
    const blockchainConfig = useBlockchainConfig(blockchain)

    const shouldShowText = !isCompact || isSelected

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className="flex shrink-0 basis-auto items-center justify-center gap-2"
            data-testid="blockchain-filter"
            data-blockchain={blockchain}
          >
            <div
              className={cn(
                "flex items-center text-sm",
                shouldShowText && "gap-2",
              )}
            >
              <div className="relative h-5 w-5">
                {isAllBlockchain ? (
                  <Image
                    className="h-full w-full"
                    imageSrc="/images/blockchains/all-chains.svg"
                    alt="All blockchains icon"
                  />
                ) : (
                  <BlockchainIcon blockchain={blockchain as Blockchain} />
                )}
              </div>
              <AnimatePresence initial={false} mode="wait">
                {shouldShowText && (
                  <motion.div
                    key={blockchain}
                    initial={{ width: 0, opacity: 0 }}
                    animate={{ width: "auto", opacity: 1 }}
                    exit={{ width: 0, opacity: 0 }}
                    transition={{ type: "spring", bounce: 0, duration: 0.3 }}
                    className="flex items-center gap-2 overflow-hidden whitespace-nowrap"
                  >
                    {isAllBlockchain ? (
                      <Trans>All</Trans>
                    ) : (
                      <Trans>{blockchainConfig?.name}</Trans>
                    )}
                    {!isAllBlockchain &&
                      blockchainConfig?.isTestnet &&
                      !isComingSoon && (
                        <span className="shrink-0 grow-0 basis-auto rounded-sm bg-foreground/10 px-[6px] py-1 font-bold text-[10px] text-foreground/60 uppercase leading-none tracking-[-0.1px]">
                          <Trans>T</Trans>
                        </span>
                      )}
                    {isComingSoon && (
                      <span className="shrink-0 grow-0 basis-auto rounded-sm bg-foreground/10 px-[6px] py-1 font-bold text-[10px] text-foreground/60 uppercase leading-none tracking-[-0.1px]">
                        <Trans>Soon</Trans>
                      </span>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </TooltipTrigger>
        {isCompact && !isSelected ? (
          <TooltipContent>
            {isAllBlockchain ? (
              <Trans>All Blockchains</Trans>
            ) : (
              <>
                <Trans>{blockchainConfig?.name}</Trans>{" "}
                {blockchainConfig?.isTestnet && !isComingSoon && (
                  <Trans>Testnet</Trans>
                )}
              </>
            )}
            {isComingSoon && <Trans>Soon</Trans>}
          </TooltipContent>
        ) : null}
        {!isCompact && blockchainConfig?.isTestnet && !isComingSoon && (
          <TooltipContent side="bottom" align="center">
            <Trans>Testnet</Trans>
          </TooltipContent>
        )}
      </Tooltip>
    )
  },
  (prevProps, nextProps) => {
    return (
      prevProps.blockchain === nextProps.blockchain &&
      prevProps.isComingSoon === nextProps.isComingSoon &&
      prevProps.isCompact === nextProps.isCompact &&
      prevProps.isSelected === nextProps.isSelected
    )
  },
)

export function BlockchainSelectItem({
  blockchain,
}: { blockchain: BlockchainFilterType }) {
  const isAllBlockchain = blockchain === "ALL"
  const blockchainConfig = useBlockchainConfig(blockchain)

  return (
    <SelectItem key={blockchain} value={blockchain}>
      <div className="flex items-center gap-2">
        <div className="relative h-5 w-5">
          {isAllBlockchain ? (
            <Image
              className="h-full w-full"
              imageSrc="/images/blockchains/rari.svg"
              alt="All blockchains icon"
            />
          ) : (
            <BlockchainIcon blockchain={blockchain as Blockchain} />
          )}
        </div>
        <div className="flex items-center gap-2 overflow-hidden whitespace-nowrap">
          {isAllBlockchain ? (
            <Trans>All chains</Trans>
          ) : (
            <Trans>{blockchainConfig?.name}</Trans>
          )}
          {!isAllBlockchain && blockchainConfig?.isTestnet && (
            <span className="shrink-0 grow-0 basis-auto rounded-sm bg-foreground/10 px-[6px] py-1 font-bold text-[10px] text-foreground/60 uppercase leading-none tracking-[-0.1px]">
              <Trans>T</Trans>
            </span>
          )}
        </div>
      </div>
    </SelectItem>
  )
}
